/**
 * The X / Z report JSON of pos-service (`ShiftReportOut.report` — `app/services/reports/zreport.py`)
 * read defensively for the screen. Every amount is the server's integer JPY; the tablet only
 * shows them (the printed 日計表 comes as ESC/POS bytes from the server).
 */

export interface ReportTender {
  method: string;
  labelJa: string | null;
  count: number;
  sales: number;
  refunds: number;
  net: number;
  fee: number;
}

export interface ReportFee {
  method: string;
  cardBrand: string | null;
  terminalId: string | null;
  count: number;
  amount: number;
  fee: number;
  net: number;
}

export interface ReportData {
  kind: "x" | "z";
  registerCode: string;
  registerName: string;
  openedAt: string | null;
  closedAt: string | null;
  openedByName: string | null;
  closedByName: string | null;
  generatedAt: string | null;
  sales: { count: number; gross: number; discount: number; tax: number; base: number };
  returns: { count: number; amount: number; tax: number };
  voids: { count: number; amount: number };
  pickups: { count: number; amount: number };
  net: number;
  consumptionTax: number;
  tenders: ReportTender[];
  fees: ReportFee[];
  feeTotal: number;
  taxFree: {
    exemption: { count: number; amount: number };
    refundPending: { count: number; amountIncl: number; taxAmount: number };
  };
  buylist: { cashPayouts: number; count: number };
  cash: {
    openingFloat: number;
    cashSales: number;
    cashRefunds: number;
    payIn: number;
    payOut: number;
    buylistPayouts: number;
    holdDeposits: number;
    expected: number;
    counted: number | null;
    variance: number | null;
    varianceApprovedBy: string | null;
    denominations: Array<{ value: number; count: number }>;
  };
  movements: Array<{ type: string; amount: number; reason: string | null; at: string | null }>;
  staff: Array<{ staffId: string; salesCount: number; gross: number }>;
  receipts: { first: string | null; last: string | null; count: number; gapless: boolean };
  /** 印紙税 (T13-24): paper cash receipts of the shift that needed a ¥200 stamp */
  stampTax: { receipts: number; affixed: number; amount: number };
}

type Json = Record<string, unknown>;

const obj = (value: unknown): Json =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Json) : {};
const int = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : 0;
const intOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : null;
const str = (value: unknown): string | null => (typeof value === "string" && value ? value : null);
const list = (value: unknown): Json[] => (Array.isArray(value) ? value.map(obj) : []);

export function readReport(raw: unknown): ReportData {
  const r = obj(raw);
  const sales = obj(r.sales);
  const returns = obj(r.returns);
  const voids = obj(r.voids);
  const pickups = obj(r.pickups);
  const taxFree = obj(r.taxFree);
  const exemption = obj(taxFree.exemption);
  const refund = obj(taxFree.refundPending);
  const buylist = obj(r.buylist);
  const cash = obj(r.cash);
  const receipts = obj(r.receipts);
  const stampTax = obj(r.stampTax);
  return {
    kind: r.kind === "z" ? "z" : "x",
    registerCode: str(r.registerCode) ?? "",
    registerName: str(r.registerName) ?? "",
    openedAt: str(r.openedAt),
    closedAt: str(r.closedAt),
    openedByName: str(r.openedByName),
    closedByName: str(r.closedByName),
    generatedAt: str(r.generatedAt),
    sales: {
      count: int(sales.count),
      gross: int(sales.gross),
      discount: int(sales.discount),
      tax: int(sales.tax),
      base: int(sales.base),
    },
    returns: { count: int(returns.count), amount: int(returns.amount), tax: int(returns.tax) },
    voids: { count: int(voids.count), amount: int(voids.amount) },
    pickups: { count: int(pickups.count), amount: int(pickups.amount) },
    net: int(r.net),
    consumptionTax: int(r.consumptionTax),
    tenders: list(r.tenders).map((t) => ({
      method: str(t.method) ?? "",
      labelJa: str(t.labelJa),
      count: int(t.count),
      sales: int(t.sales),
      refunds: int(t.refunds),
      net: int(t.net),
      fee: int(t.fee),
    })),
    fees: list(r.fees).map((f) => ({
      method: str(f.method) ?? "",
      cardBrand: str(f.cardBrand),
      terminalId: str(f.terminalId),
      count: int(f.count),
      amount: int(f.amount),
      fee: int(f.fee),
      net: int(f.net),
    })),
    feeTotal: int(r.feeTotal),
    taxFree: {
      exemption: { count: int(exemption.count), amount: int(exemption.amount) },
      refundPending: {
        count: int(refund.count),
        amountIncl: int(refund.amountIncl),
        taxAmount: int(refund.taxAmount),
      },
    },
    buylist: { cashPayouts: int(buylist.cashPayouts), count: int(buylist.count) },
    cash: {
      openingFloat: int(cash.openingFloat),
      cashSales: int(cash.cashSales),
      cashRefunds: int(cash.cashRefunds),
      payIn: int(cash.payIn),
      payOut: int(cash.payOut),
      buylistPayouts: int(cash.buylistPayouts),
      holdDeposits: int(cash.holdDeposits),
      expected: int(cash.expected),
      counted: intOrNull(cash.counted),
      variance: intOrNull(cash.variance),
      varianceApprovedBy: str(cash.varianceApprovedBy),
      denominations: list(cash.denominations).map((d) => ({
        value: int(d.value),
        count: int(d.count),
      })),
    },
    movements: list(r.movements).map((m) => ({
      type: str(m.type) ?? "",
      amount: int(m.amount),
      reason: str(m.reason),
      at: str(m.at),
    })),
    staff: list(r.staff).map((s) => ({
      staffId: str(s.staffId) ?? "",
      salesCount: int(s.salesCount),
      gross: int(s.gross),
    })),
    receipts: {
      first: str(receipts.first),
      last: str(receipts.last),
      count: int(receipts.count),
      gapless: receipts.gapless !== false,
    },
    stampTax: {
      receipts: int(stampTax.receipts),
      affixed: int(stampTax.affixed),
      amount: int(stampTax.amount),
    },
  };
}

/** The card-terminal rows of the report (per terminal + brand, as the server added them up). */
export function terminalRows(report: ReportData): ReportFee[] {
  return report.fees
    .filter((fee) => fee.method === "edc_external")
    .sort((a, b) => (a.terminalId ?? "").localeCompare(b.terminalId ?? ""));
}

export type VarianceTone = "ok" | "warn" | "problem";

/** 0 → green; below the manager threshold → yellow; at / over it → red (design/05 §7). */
export function varianceTone(variance: number, threshold: number): VarianceTone {
  if (variance === 0) {
    return "ok";
  }
  return Math.abs(variance) >= threshold ? "problem" : "warn";
}
